/**
 * Design Tokens
 * 
 * Centralized design system tokens for the entire application.
 * 
 * IMPORTANT: This file provides TypeScript access to design tokens.
 * The actual values are defined as CSS variables in index.css.
 * 
 * To change brand colors:
 * 1. Update CSS variables in src/index.css
 * 2. These tokens will automatically reflect the changes
 * 
 * Usage:
 * - In CSS/Tailwind: Use classes like bg-primary-500, text-accent-400
 * - In JS/TS: Import these tokens for programmatic access
 */

// =============================================================================
// CSS VARIABLE HELPERS
// =============================================================================

/**
 * Get the CSS variable value for a color
 * @example getCssVar('--color-primary-500') // Returns 'rgb(var(--color-primary-500))'
 */
export const getCssVar = (name: string): string => `rgb(var(${name}))`;

/**
 * Get the CSS variable value with opacity
 * @example getCssVarWithOpacity('--color-primary-500', 0.5) // Returns 'rgb(var(--color-primary-500) / 0.5)'
 */
export const getCssVarWithOpacity = (name: string, opacity: number): string => 
    `rgb(var(${name}) / ${opacity})`;

// =============================================================================
// BRAND COLORS (Reference values - actual values from CSS variables)
// =============================================================================

export const colors = {
    // Primary Brand (HAAT Red)
    // To change: Edit --color-primary-* in index.css
    primary: {
        50: getCssVar('--color-primary-50'),
        100: getCssVar('--color-primary-100'),
        200: getCssVar('--color-primary-200'),
        300: getCssVar('--color-primary-300'),
        400: getCssVar('--color-primary-400'),
        500: getCssVar('--color-primary-500'),  // Main brand color
        600: getCssVar('--color-primary-600'),
        700: getCssVar('--color-primary-700'),
        800: getCssVar('--color-primary-800'),
        900: getCssVar('--color-primary-900'),
        DEFAULT: getCssVar('--color-primary-500'),
    },

    // Accent (HAAT Gold/Yellow)
    // To change: Edit --color-accent-* in index.css
    accent: {
        50: getCssVar('--color-accent-50'),
        100: getCssVar('--color-accent-100'),
        200: getCssVar('--color-accent-200'),
        300: getCssVar('--color-accent-300'),
        400: getCssVar('--color-accent-400'),  // Main accent color
        500: getCssVar('--color-accent-500'),
        600: getCssVar('--color-accent-600'),
        700: getCssVar('--color-accent-700'),
        800: getCssVar('--color-accent-800'),
        900: getCssVar('--color-accent-900'),
        DEFAULT: getCssVar('--color-accent-400'),
    },

    // Gray Scale
    gray: {
        50: getCssVar('--color-gray-50'),
        100: getCssVar('--color-gray-100'),
        200: getCssVar('--color-gray-200'),
        300: getCssVar('--color-gray-300'),
        400: getCssVar('--color-gray-400'),
        500: getCssVar('--color-gray-500'),
        600: getCssVar('--color-gray-600'),
        700: getCssVar('--color-gray-700'),
        800: getCssVar('--color-gray-800'),
        900: getCssVar('--color-gray-900'),
    },

    // Semantic Colors
    success: {
        light: '#dcfce7',
        main: getCssVar('--color-success'),
        dark: '#15803d',
    },
    warning: {
        light: '#fef3c7',
        main: getCssVar('--color-warning'),
        dark: '#b45309',
    },
    error: {
        light: '#fee2e2',
        main: getCssVar('--color-error'),
        dark: '#b91c1c',
    },
    info: {
        light: '#dbeafe',
        main: getCssVar('--color-info'),
        dark: '#1d4ed8',
    },
} as const;

// =============================================================================
// RAW HEX VALUES (For places that REQUIRE hex, like canvas, charts)
// =============================================================================

export const rawColors = {
    primary: '#c00035',
    primaryDark: '#800025',
    primaryLight: '#ff6489',
    accent: '#f8cc05',
    accentDark: '#ca8a04',
} as const;

// =============================================================================
// SPACING
// =============================================================================

export const spacing = {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
    fontFamily: {
        sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'Fira Code', Consolas, Monaco, 'Andale Mono', monospace",
    },
    fontSize: {
        xs: '0.75rem',    // 12px
        sm: '0.875rem',   // 14px
        base: '1rem',     // 16px
        lg: '1.125rem',   // 18px
        xl: '1.25rem',    // 20px
        '2xl': '1.5rem',  // 24px
        '3xl': '1.875rem',// 30px
        '4xl': '2.25rem', // 36px
    },
    fontWeight: {
        normal: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
    },
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const shadows = {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    sidebar: '4px 0 24px -2px rgba(0,0,0,0.2)',
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================

export const borderRadius = {
    none: '0',
    sm: '0.125rem',   // 2px
    base: '0.25rem',  // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    '3xl': '1.5rem',  // 24px
    full: '9999px',
} as const;

// =============================================================================
// BREAKPOINTS
// =============================================================================

export const breakpoints = {
    xs: 0,
    sm: 480,
    md: 640,
    lg: 768,
    xl: 1024,
    '2xl': 1280,
    '3xl': 1536,
} as const;

// =============================================================================
// SIDEBAR
// =============================================================================

export const sidebar = {
    width: {
        expanded: 280,
        collapsed: 76,
    },
    transition: {
        duration: 300,
        easing: 'ease-out',
    },
} as const;

// =============================================================================
// Z-INDEX
// =============================================================================

export const zIndex = {
    dropdown: 10,
    sticky: 20,
    header: 30,
    sidebar: 40,
    overlay: 50,
    modal: 9999,
    tooltip: 99999,
} as const;

// =============================================================================
// THEME OBJECT (for easy access)
// =============================================================================

export const theme = {
    colors,
    spacing,
    typography,
    shadows,
    borderRadius,
    breakpoints,
    sidebar,
    zIndex,
} as const;

export type Theme = typeof theme;

export default theme;

